import { HttpClient } from '@angular/common/http'
import { Injectable, Inject } from '@angular/core'
import { ParamMap } from '@angular/router'
import {
  ApiCallModel,
  ContainerPartial,
  EntityHubLayout,
  FilterablePartial,
  GridContainerPartial,
  MultipleSectionsViewModel
} from '@flocasts/experience-service-types'
import { Observable, catchError, of, switchMap, shareReplay, combineLatest, startWith } from 'rxjs'
import { EXPERIENCE_API_ENTITY_HUB_BASE_PATH } from '../app.config'
import { DataService } from '../data/data.service'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import {
  EXP_ENTITY_HUBS_SCHEDULE_REDESIGN_2025_ID,
  EXP_ENTITY_HUBS_SCHEDULE_REDESIGN_2025_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
  EXP_NEXT_GEN_NAVIGATION_V2,
  EXP_NEXT_GEN_NAVIGATION_V2_VARIATION,
  FEAT_VARSITY_LEAGUE_EVENTS_ID,
  FEAT_VARSITY_LEAGUE_EVENTS_VARIATION
} from '../shared/experimentation/experiment.model'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import { CacheDataService } from '../singleton-services/cache-data.service'
import { getEntityHubHttpParams, getEntityHubLayoutEndpoint } from './entity-hub.utility'
import { VerticalService } from '../singleton-services/vertical.service'

@Injectable()
export class EntityHubDataService {
  constructor(
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly cacheDataService: CacheDataService,
    private readonly dataService: DataService,
    private readonly http: HttpClient,
    private readonly experimentationService: ExperimentationService,
    private readonly verticalService: VerticalService
  ) {}

  public inNavV2Variation$ = this.experimentationService
    .isInVariation(EXP_NEXT_GEN_NAVIGATION_V2, EXP_NEXT_GEN_NAVIGATION_V2_VARIATION)
    .pipe(shareReplay(1))

  public inVarsityLeagueEventsVariation$ = this.experimentationService
    .isInVariation(FEAT_VARSITY_LEAGUE_EVENTS_ID, FEAT_VARSITY_LEAGUE_EVENTS_VARIATION)
    .pipe(shareReplay(1))

  public inScheduleRedesign2025Variation$ = this.experimentationService
    .isInVariation(EXP_ENTITY_HUBS_SCHEDULE_REDESIGN_2025_ID, EXP_ENTITY_HUBS_SCHEDULE_REDESIGN_2025_VARIATION)
    .pipe(shareReplay(1))
  public readonly isNextGenEventHub$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
    false
  )
  public readonly isNextGenEventHubShort$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
    false
  )
  public isNextGen$ = combineLatest([this.isNextGenEventHubShort$, this.isNextGenEventHub$]).pipe(
    switchMap(([nextGenShort, nextGen]) => {
      return of(nextGen || nextGenShort)
    }),
    shareReplay(1)
  )

  public siteId$ = this.verticalService.siteId$

  public getEntityHubLayoutWithCacheWrapper(
    id: number,
    tab: string | null | undefined,
    queryParams: ParamMap | null,
    isNextGenEventHub?: boolean
  ): Observable<EntityHubLayout | undefined> {
    return combineLatest([
      this.inNavV2Variation$,
      this.inVarsityLeagueEventsVariation$,
      this.inScheduleRedesign2025Variation$,
      this.siteId$
    ]).pipe(
      switchMap(([useCoreApiTabs, showLeagueEvents, useCalendarLayoutVersion, site]) => {
        let params = getEntityHubHttpParams(
          queryParams,
          useCoreApiTabs,
          undefined,
          showLeagueEvents,
          useCalendarLayoutVersion
        )
        const search = queryParams?.get('search')
        params = search ? params.set('search', search) : params
        const playing = queryParams?.get('playing')
        params = playing ? params.set('playing', playing) : params
        params = params.set('site_id', site)

        const endpoint = getEntityHubLayoutEndpoint(id, tab)

        return this.cacheDataService.wrapper<EntityHubLayout>(endpoint, params, true).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      })
    )
  }

  public getEntityHubLayout(
    id: number,
    tab: string | null | undefined,
    queryParams: ParamMap | null,
    isNextGenEventHub?: boolean
  ): Observable<EntityHubLayout | undefined> {
    return combineLatest([this.inNavV2Variation$, this.inVarsityLeagueEventsVariation$]).pipe(
      switchMap(([useCoreApiTabs, showLeagueEvents]) => {
        const params = getEntityHubHttpParams(queryParams, useCoreApiTabs, showLeagueEvents, isNextGenEventHub)
        const endpoint = getEntityHubLayoutEndpoint(id, tab)
        return this.http.get<EntityHubLayout>(endpoint, { params }).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      })
    )
  }

  public getEntityHubPartial(
    id: number,
    tab: string | null,
    queryParams: ParamMap | null
  ): Observable<FilterablePartial | GridContainerPartial | MultipleSectionsViewModel | ContainerPartial | undefined> {
    if (!tab) return of(undefined)

    return combineLatest([
      this.inNavV2Variation$,
      this.inVarsityLeagueEventsVariation$,
      this.siteId$,
      this.isNextGen$
    ]).pipe(
      switchMap(([useCoreApiTabs, showLeagueEvents, site, isNextGenEventHub]) => {
        let params = getEntityHubHttpParams(queryParams, useCoreApiTabs, undefined, showLeagueEvents, isNextGenEventHub)
        const search = queryParams?.get('search')
        params = search ? params.set('search', search) : params
        params = params.set('site_id', site.toString())

        const endpoint = `${EXPERIENCE_API_ENTITY_HUB_BASE_PATH}/${id}/${tab}/partial`

        if (queryParams?.get('viewMode') === 'minimal') {
          return this.cacheDataService
            .wrapper<FilterablePartial | GridContainerPartial | MultipleSectionsViewModel | ContainerPartial>(
              endpoint,
              params,
              true
            )
            .pipe(
              catchError(err => {
                this.logger.error(err.message, err.error)
                return of(undefined)
              })
            )
        }

        return this.http
          .get<FilterablePartial | GridContainerPartial | MultipleSectionsViewModel | ContainerPartial>(endpoint, {
            params
          })
          .pipe(
            catchError(err => {
              this.logger.error(err.message, err.error)
              return of(undefined)
            })
          )
      })
    )
  }

  public getEntityHubPaginatedPartial(endpoint: string): Observable<FilterablePartial | undefined> {
    return this.dataService.get<FilterablePartial>(endpoint)
  }

  public favoriteAndRefetchEntityHubLayout(
    apiCall: ApiCallModel,
    id: number | null,
    tab: string | null | undefined,
    queryParams: ParamMap | null
  ): Observable<EntityHubLayout | undefined> {
    return this.dataService.request<undefined>(apiCall).pipe(
      switchMap(() => {
        return this.isNextGen$.pipe(
          switchMap(isNextGen => {
            if (!!id) return this.getEntityHubLayout(id, tab, queryParams, isNextGen)

            return of(undefined)
          })
        )
      })
    )
  }
}
